import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { PaperCode, ApiPaperCode, AnswerSheet, ApiAnswerSheet, ApiEvaluationReport } from './types';
import axiosInstance from '../../redux/axios';
import { logout } from '../auth/AuthSlice';

interface EvaluatorState {
    papers: PaperCode[];
    answerSheets: AnswerSheet[];
    answerSheetsTotal: number;
    answerSheetsCompleted: number;
    selectedPaperId: string | null;
    loading: boolean;
    error: string | null;
    evaluating: boolean;
    evaluationError: string | null;
    currentReport: ApiEvaluationReport | null;
    reportLoading: boolean;
    reportError: string | null;
    currentAnswerSheetPage: number;
}

const initialState: EvaluatorState = {
    papers: [],
    answerSheets: [],
    answerSheetsTotal: 0,
    answerSheetsCompleted: 0,
    selectedPaperId: null,
    loading: false,
    error: null,
    evaluating: false,
    evaluationError: null,
    currentReport: null,
    reportLoading: false,
    reportError: null,
    currentAnswerSheetPage: 1,
};

export interface FetchPapersParams {
    limit?: number;
    offset?: number;
    search?: string;
}

export const fetchPaperCodes = createAsyncThunk(
    'evaluator/fetchPaperCodes',
    async (params: FetchPapersParams | undefined, { rejectWithValue, getState }) => {
        try {
            const state = getState() as any;
            const token = state.auth?.access_token;

            const API_URL = import.meta.env.VITE_REACT_APP_API_URL || 'https://ai-evaluator.srv1240507.hstgr.cloud';
            const limit = params?.limit || 50;
            const offset = params?.offset || 0;
            let queryParams = `limit=${limit}&offset=${offset}`;

            if (params?.search) queryParams += `&search=${encodeURIComponent(params.search)}`;

            const response = await axiosInstance.get<{ data: ApiPaperCode[], total: number }>(
                `${API_URL}/api/paper-codes?${queryParams}`,
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined
                }
            );

            // Map API response to UI model
            const mappedPapers: PaperCode[] = response.data.data.map((apiPaper) => ({
                id: apiPaper.paper_code_id,
                code: apiPaper.paper_code || apiPaper.code || "Unknown",
                subject: apiPaper.subject_name ? (apiPaper.subject_name.charAt(0).toUpperCase() + apiPaper.subject_name.slice(1)) : "Unknown Subject",
                date: apiPaper.created_at,
                totalCopies: apiPaper.total || 0,
                evaluated: apiPaper.total_evaluated || 0,
                pending: (apiPaper.total || 0) - (apiPaper.total_evaluated || 0),
                status: (apiPaper.total && apiPaper.total_evaluated === apiPaper.total) ? 'completed' :
                    (apiPaper.total_evaluated && apiPaper.total_evaluated > 0) ? 'in-progress' :
                        apiPaper.is_question_paper ? 'in-progress' : 'pending',
                isQuestionPaper: apiPaper.is_question_paper,
                questionPaperUrl: apiPaper.question_paper_url || undefined,
                strictnessRules: apiPaper.strictness_rules || "none" // Use "none" or empty string as default if you prefer
            }));

            return mappedPapers;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch paper codes');
        }
    }
);

export const fetchAnswerSheets = createAsyncThunk(
    'evaluator/fetchAnswerSheets',
    async ({ paperId, limit = 50, offset = 0, status }: { paperId: string; limit?: number; offset?: number; status?: string }, { rejectWithValue, getState }) => {
        try {
            const state = getState() as any;
            const token = state.auth?.access_token;
            const API_URL = import.meta.env.VITE_REACT_APP_API_URL || 'https://ai-evaluator.srv1240507.hstgr.cloud';

            let queryParams = `paper_code_id=${paperId}&limit=${limit}&offset=${offset}`;
            if (status && status !== 'all') {
                queryParams += `&status=${status}`;
            }

            const response = await axiosInstance.get<{ data: ApiAnswerSheet[], total: number, total_completed: number }>(
                `${API_URL}/api/file-history?${queryParams}`,
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined
                }
            );
            const mappedSheets: AnswerSheet[] = response.data.data.map(sheet => ({
                id: sheet.file_id,
                fileName: sheet.file_name,
                status: sheet.status,
                score: sheet.overall_score || 0,
                maxScore: sheet.max_score || 0,
                percentage: sheet.percentage || 0,
                remarks: "Pending Evaluation", // Not available
                date: sheet.created_at,
                s3Url: sheet.s3_url,
                subjectName: sheet.subject_name,
                paperCode: sheet.paper_code
            }));

            return {
                data: mappedSheets,
                total: response.data.total || 0,
                totalCompleted: response.data.total_completed || 0
            };
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch answer sheets');
        }
    }
);

export const startEvaluation = createAsyncThunk(
    'evaluator/startEvaluation',
    async ({ paperId, fileUrl, rules }: { paperId: string; fileUrl: string; rules: string }, { rejectWithValue }) => {
        try {
            const API_URL = import.meta.env.VITE_REACT_APP_API_URL || 'https://ai-evaluator.srv1240507.hstgr.cloud';
            const response = await axiosInstance.post(`${API_URL}/api/evaluation/upload-question-paper`, {
                paper_code_id: paperId,
                question_paper_url: fileUrl,
                strictness_rules: rules || "none"
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to start evaluation');
        }
    }
);

export const evaluateAnswerCopies = createAsyncThunk(
    'evaluator/evaluateAnswerCopies',
    async ({ paperCodeId, fileIds }: { paperCodeId: string; fileIds: string[] }, { rejectWithValue }) => {
        try {
            const API_URL = import.meta.env.VITE_REACT_APP_API_URL || 'https://ai-evaluator.srv1240507.hstgr.cloud';
            const response = await axiosInstance.post(`${API_URL}/api/evaluation/evaluate-answer-copy`, {
                paper_code_id: paperCodeId,
                file_ids: fileIds
            });
            return response.data;
        } catch (error: any) {
            const data = error.response?.data;
            return rejectWithValue(
                data?.error?.message || data?.message || 'Failed to start evaluation. Please try again.'
            );
        }
    }
);

export const fetchEvaluationStatus = createAsyncThunk(
    'evaluator/fetchEvaluationStatus',
    async (paperId: string, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get<{ success: boolean; evaluation_pending: boolean }>(
                `/api/evaluation/status/${paperId}`
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch evaluation status');
        }
    }
);

export const updateStrictnessRules = createAsyncThunk(
    'evaluator/updateStrictnessRules',
    async ({ paperId, rules }: { paperId: string; rules: string }, { rejectWithValue }) => {
        try {
            const API_URL = import.meta.env.VITE_REACT_APP_API_URL || 'https://ai-evaluator.srv1240507.hstgr.cloud';
            const response = await axiosInstance.put(`${API_URL}/api/evaluation/update-strictness/${paperId}`, {
                strictness_rules: rules
            });
            return response.data;
        } catch (error: any) {
            const data = error.response?.data;
            return rejectWithValue(
                data?.error?.message || data?.message || 'Failed to update rules. Please try again.'
            );
        }
    }
);

export const downloadCsv = createAsyncThunk(
    'evaluator/downloadCsv',
    async (paperCodeId: string, { rejectWithValue }) => {
        try {
            const API_URL = import.meta.env.VITE_REACT_APP_API_URL || 'https://ai-evaluator.srv1240507.hstgr.cloud';
            const response = await axiosInstance.get<{ success: boolean; message: string; csv_url: string }>(
                `${API_URL}/api/evaluation/download/csv/${paperCodeId}`
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to download CSV');
        }
    }
);

export const updateAnswerMarks = createAsyncThunk(
    'evaluator/updateAnswerMarks',
    async (
        { evaluationId, answerId, marks }: { evaluationId: string; answerId: string; marks: number },
        { rejectWithValue, getState }
    ) => {
        try {
            const state = getState() as any;
            const token = state.auth?.access_token;
            const API_URL = import.meta.env.VITE_REACT_APP_API_URL || 'https://ai-evaluator.srv1240507.hstgr.cloud';

            const response = await axiosInstance.patch<{
                success: boolean;
                message: string;
                data: {
                    answer_id: string;
                    evaluation_id: string;
                    question_number: string;
                    marks_obtained: number;
                    max_marks: number;
                    overall_score: number;
                    max_score: number;
                    percentage: number;
                };
            }>(
                `${API_URL}/api/evaluation/${evaluationId}/answer/${answerId}/marks`,
                { marks_obtained: marks },
                { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.detail?.[0]?.msg ||
                error.response?.data?.message ||
                'Failed to update marks'
            );
        }
    }
);

export const fetchEvaluationReport = createAsyncThunk(
    'evaluator/fetchEvaluationReport',
    async (fileId: string, { rejectWithValue, getState }) => {
        try {
            const state = getState() as any;
            const token = state.auth?.access_token;
            const API_URL = import.meta.env.VITE_REACT_APP_API_URL || 'https://ai-evaluator.srv1240507.hstgr.cloud';

            const response = await axiosInstance.get<ApiEvaluationReport>(
                `${API_URL}/api/evaluation/file/${fileId}`,
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined
                }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch evaluation report');
        }
    }
);

const evaluatorSlice = createSlice({
    name: 'evaluator',
    initialState,
    reducers: {
        setPapers: (state, action: PayloadAction<PaperCode[]>) => {
            state.papers = action.payload;
        },
        updatePaperStatus: (state, action: PayloadAction<{ id: string; status: PaperCode['status'] }>) => {
            const paper = state.papers.find(p => p.id === action.payload.id);
            if (paper) {
                paper.status = action.payload.status;
            }
        },
        setSelectedPaperId: (state, action: PayloadAction<string | null>) => {
            if (state.selectedPaperId !== action.payload) {
                state.selectedPaperId = action.payload;
                state.currentAnswerSheetPage = 1;
            }
        },
        setCurrentAnswerSheetPage: (state, action: PayloadAction<number>) => {
            state.currentAnswerSheetPage = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPaperCodes.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPaperCodes.fulfilled, (state, action) => {
                state.loading = false;
                state.papers = action.payload;
            })
            .addCase(fetchPaperCodes.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Fetch Answer Sheets Cases
            .addCase(fetchAnswerSheets.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.answerSheets = [];
            })
            .addCase(fetchAnswerSheets.fulfilled, (state, action) => {
                state.loading = false;
                state.answerSheets = action.payload.data;
                state.answerSheetsTotal = action.payload.total;
                state.answerSheetsCompleted = action.payload.totalCompleted;

                // Update paper stats
                const paper = state.papers.find(p => p.id === action.meta.arg.paperId);
                if (paper) {
                    paper.totalCopies = action.payload.total;
                    paper.evaluated = action.payload.totalCompleted;
                    paper.pending = paper.totalCopies - paper.evaluated;
                }
            })
            .addCase(fetchAnswerSheets.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Start Evaluation Cases
            .addCase(startEvaluation.pending, (state: any) => {
                state.evaluating = true;
                state.evaluationError = null;
            })
            .addCase(startEvaluation.fulfilled, (state: any, action) => {
                state.evaluating = false;
                const paper = state.papers.find((p: any) => p.id === action.meta.arg.paperId);
                if (paper) {
                    paper.status = 'in-progress';
                    paper.isQuestionPaper = true;
                    if (action.meta.arg.fileUrl) paper.questionPaperUrl = action.meta.arg.fileUrl;
                    if (action.meta.arg.rules) paper.strictnessRules = action.meta.arg.rules;
                }
            })
            .addCase(startEvaluation.rejected, (state: any, action) => {
                state.evaluating = false;
                state.evaluationError = action.payload as string;
            })
            // Bulk Evaluation Cases
            .addCase(evaluateAnswerCopies.pending, (state: any) => {
                state.evaluating = true;
                state.evaluationError = null;
            })
            .addCase(evaluateAnswerCopies.fulfilled, (state: any) => {
                state.evaluating = false;
            })
            .addCase(evaluateAnswerCopies.rejected, (state: any, action) => {
                state.evaluating = false;
                state.evaluationError = action.payload as string;
            })
            // Update Strictness Cases
            .addCase(updateStrictnessRules.pending, (state: any) => {
                state.evaluating = true;
                state.evaluationError = null;
            })
            .addCase(updateStrictnessRules.fulfilled, (state: any, action) => {
                state.evaluating = false;
                const paper = state.papers.find((p: any) => p.id === action.meta.arg.paperId);
                if (paper) {
                    paper.strictnessRules = action.meta.arg.rules;
                }
            })
            .addCase(updateStrictnessRules.rejected, (state: any, action) => {
                state.evaluating = false;
                state.evaluationError = action.payload as string;
            })
            // Fetch Evaluation Report Cases
            .addCase(fetchEvaluationReport.pending, (state: any) => {
                state.reportLoading = true;
                state.reportError = null;
                state.currentReport = null;
            })
            .addCase(fetchEvaluationReport.fulfilled, (state: any, action) => {
                state.reportLoading = false;
                state.currentReport = action.payload;
            })
            .addCase(fetchEvaluationReport.rejected, (state: any, action) => {
                state.reportLoading = false;
                state.reportError = action.payload as string;
            })
            // Update Answer Marks Cases
            .addCase(updateAnswerMarks.fulfilled, (state: any, action) => {
                if (!state.currentReport) return;
                const payload = action.payload;
                const answer = state.currentReport.answers.find(
                    (a: any) => a.answer_id === payload.answer_id
                );
                if (answer) {
                    answer.marks_obtained = payload.marks_obtained;
                }
                state.currentReport.overall_score = payload.overall_score;
                state.currentReport.max_score = payload.max_score;
                state.currentReport.percentage = payload.percentage;
            })
            // Reset evaluator state on logout
            .addCase(logout, () => initialState);
    }
});

export const { setPapers, updatePaperStatus, setSelectedPaperId, setCurrentAnswerSheetPage } = evaluatorSlice.actions;
export default evaluatorSlice.reducer;
